import { push } from 'connected-react-router'
import {
  getBadgeCounts,
  getMostRecentReadMessageForConversation,
  getNumberOfUnreadConversations,
} from 'conversations/modules/conversation/selectors'
import { byId } from 'conversations/modules/endUser/selectors'
import { getAppServices } from 'conversations/services'
import { captureError } from 'conversations/utils/logRocket'
import { isDefined, isUndefined } from 'conversations/utils/typescriptHelpers'
import { Epic } from 'redux-observable'
import { empty, from, merge, of } from 'rxjs'
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  pluck,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import {
  closeAllConversationsAsync,
  decrementBadgeCount,
  fetchAllConversationsAsync,
  fetchConversationAsync,
  fetchConversationLastSyncedAsync,
  fetchCounts,
  incrementBadgeCount,
  maybeFetchConversationFromClipboard,
  receiveConversationUpdateFromWebsocket,
  receiveCounts,
  receiveManuallyMarkedAsRead,
  receiveMessageReadReceipt,
  receiveMostRecentlyReadMessageForConversation,
  syncConversationAsync,
  updateConversationStatusAsync,
  updatePendingReadReceipt,
  uploadFileAsync,
} from 'shared/store/actions/conversation'
import {
  bulkMessageReadAsync,
  fetchMessagesAsync,
  receiveMessage,
} from 'shared/store/actions/message'
import { MessageWithId } from 'shared/typings/messages/Message'
import { Status } from 'shared/typings/messages/MessageRecipientStatus'
import { UserIdType } from 'shared/typings/UserIdType'
import { fetchConversationMaybe } from 'shared/utils/url'

import { receiveConversationBadgeCounts } from '@driftt/traffic-control'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

import { getMessageById } from '../message/selectors'

import { createConversationAsync } from './actions'
import * as api from './api'
import {
  endUserReceivedMessage,
  isMostRecentlyReadMessageByEndUser,
  isNotEndUserMessage,
  isReadableMessageWithPendingReceipt,
  newReceiptIsMostRecent,
} from './helpers'

export const fetchAllConversations: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf(fetchAllConversationsAsync.request)),
    mergeMap((action) =>
      from(
        api.searchConversations(
          {
            ...action.payload.query,
            cursor: action.payload.cursor,
          },
          action.payload.subscriptionId
        )
      ).pipe(
        map((res) =>
          fetchAllConversationsAsync.success({
            response: res,
            viewType: action.payload.viewType,
            refreshState: action.payload.refreshState,
            subscriptionId: action.payload.subscriptionId,
          })
        ),
        catchError((error: Error) =>
          of(fetchAllConversationsAsync.failure({ error, viewType: action.payload.viewType }))
        )
      )
    )
  )
}

export const updateConversationStatusEpic: Epic<RootAction, RootAction, RootState> = (action$) => {
  return action$.pipe(
    filter(isActionOf(updateConversationStatusAsync.request)),
    switchMap((action) =>
      from(
        api.updateConversationStatus(
          action.payload.conversationId,
          action.payload.conversationStatus
        )
      ).pipe(
        map(updateConversationStatusAsync.success),
        catchError((error) => of(updateConversationStatusAsync.failure(error)))
      )
    )
  )
}

export const sendUnreadMessageCount: Epic<RootAction, RootAction, RootState> = (action$, state) =>
  action$.pipe(
    filter(
      isActionOf([
        bulkMessageReadAsync.success,
        receiveConversationUpdateFromWebsocket,
        receiveManuallyMarkedAsRead,
      ])
    ),
    mergeMap(() => {
      const numUnreadConversations = getNumberOfUnreadConversations(state.value)
      getAppServices().badges.showBadgeIconNumber(numUnreadConversations)
      return empty()
    })
  )

export const receiveMessageEpic: Epic<RootAction, RootAction, RootState> = (action$, state) =>
  action$.pipe(
    filter(isActionOf(receiveMessage)),
    pluck('payload'),
    filter(isReadableMessageWithPendingReceipt(state)),
    mergeMap((message) =>
      of(
        updatePendingReadReceipt({
          conversationId: message.conversationId,
          messageId: undefined,
        }),
        receiveMostRecentlyReadMessageForConversation({
          conversationId: message.conversationId,
          messageId: message.id,
        })
      )
    )
  )

export const receiveMessageReadReceiptEpic: Epic<RootAction, RootAction, RootState> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf(receiveMessageReadReceipt)),
    pluck('payload'),
    filter(endUserReceivedMessage(state)),
    filter(newReceiptIsMostRecent(state)),
    mergeMap((payload) => {
      const message$ = of(
        getMessageById(payload.conversationId, payload.messageId)(state.value) as MessageWithId
      )

      const messageExists$ = message$.pipe(
        filter(isDefined),
        filter(isNotEndUserMessage(state)(payload)),
        filter(() => payload.status === Status.Read),
        map(() => {
          return receiveMostRecentlyReadMessageForConversation({
            conversationId: payload.conversationId,
            messageId: payload.messageId,
          })
        })
      )

      const messageDoesNotExist$ = message$.pipe(
        filter(isUndefined),
        map(() =>
          updatePendingReadReceipt({
            conversationId: payload.conversationId,
            messageId: payload.messageId,
          })
        )
      )

      return merge(messageExists$, messageDoesNotExist$)
    })
  )

export const updateMostRecentlyReadAfterMessageListFetch: Epic<
  RootAction,
  RootAction,
  RootState
> = (action$, state) =>
  action$.pipe(
    filter(isActionOf(fetchMessagesAsync.success)),
    pluck('payload'),
    mergeMap((load) => {
      const {
        paginatedMessages: { data: messages },
        conversationId,
      } = load
      const mostRecentMessage = getMostRecentReadMessageForConversation(conversationId)(state.value)
      return from(messages).pipe(
        filter(isMostRecentlyReadMessageByEndUser(mostRecentMessage)),
        take(1),
        map((message) =>
          receiveMostRecentlyReadMessageForConversation({
            conversationId: conversationId,
            messageId: message.id,
          })
        )
      )
    })
  )

export const uploadFile: Epic<RootAction, RootAction, RootState, Services> = (action$) => {
  return action$.pipe(
    filter(isActionOf(uploadFileAsync.request)),
    mergeMap(({ payload: { file, conversationId, uploadId } }) =>
      from(api.uploadFile(conversationId, file)).pipe(
        map((attachment) => uploadFileAsync.success({ attachment, uploadId })),
        catchError((error) =>
          of(uploadFileAsync.failure({ error, uploadId, conversationId, file }))
        ),
        takeUntil(action$.pipe(filter(isActionOf(uploadFileAsync.cancel))))
      )
    )
  )
}

export const fetchConversationLastSynced: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf(fetchConversationLastSyncedAsync.request)),
    switchMap((action) => {
      return from(api.fetchLastSyncedTime(action.payload.id)).pipe(
        map(fetchConversationLastSyncedAsync.success),
        catchError((error: Error) => {
          return of(fetchConversationLastSyncedAsync.failure(error))
        }),
        takeUntil(action$.pipe(filter(isActionOf(fetchConversationLastSyncedAsync.cancel))))
      )
    })
  )
}

export const createConversation: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf(createConversationAsync.request)),
    switchMap(({ payload }) => {
      const endUserType = byId(state.value)[payload.endUserId]?.type || UserIdType.LEAD
      const messageToCreate = {
        ...payload.message,
        to: {
          id: payload.endUserId,
          type: endUserType,
        },
        tags: payload.tags,
      }

      return from(api.createConversation(messageToCreate)).pipe(
        map((messageWithId) => push(`/conversations/${messageWithId.conversationId}`, {}))
      )
    })
  )

export const openClipboardConversation: Epic<RootAction, any> = (action$) => {
  return action$.pipe(
    filter(isActionOf(maybeFetchConversationFromClipboard)),
    mergeMap(() =>
      from(navigator.clipboard.readText()).pipe(
        filter<string | undefined, string>(isDefined),
        map(fetchConversationMaybe),
        filter<string | undefined, string>(isDefined),
        map((url: string) => push(url))
      )
    )
  )
}

export const syncConversation: Epic<RootAction, RootAction, RootState, Services> = (action$) => {
  return action$.pipe(
    filter(isActionOf(syncConversationAsync.request)),
    debounceTime(1000),
    switchMap((action) =>
      from(api.syncConversation(action.payload)).pipe(
        map(syncConversationAsync.success),
        catchError((error: Error) => of(syncConversationAsync.failure(error))),
        takeUntil(action$.pipe(filter(isActionOf(syncConversationAsync.cancel))))
      )
    )
  )
}

export const updateTrafficControlBadgeCount: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) => {
  return action$.pipe(
    filter(isActionOf([incrementBadgeCount, decrementBadgeCount])),
    map(() => {
      const badgeCounts = getBadgeCounts()(state.value)
      const total = Object.values(badgeCounts).reduce(
        (acc, conversations) => acc + Object.keys(conversations).length,
        0
      )
      return receiveConversationBadgeCounts(total)
    })
  )
}

export const fetchConversationRequest: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf(fetchConversationAsync.request)),
    switchMap((action) => {
      return from(api.fetchConversation(action.payload)).pipe(
        map((conversation) => fetchConversationAsync.success(conversation)),
        catchError((error: Error) => {
          captureError(error, 'Refetch convo failed')
          return of(fetchConversationAsync.failure(error))
        })
      )
    })
  )
}

export const fetchSectionCounts: Epic<RootAction, RootAction, RootState, Services> = (action$) => {
  return action$.pipe(
    filter(isActionOf(fetchCounts)),
    mergeMap((action) => {
      const { subscriptionId, inboxId, sectionUuid } = action.payload
      return from(api.conversationListCounts(subscriptionId, inboxId)).pipe(
        map((counts) => receiveCounts({ [sectionUuid]: counts })),
        catchError((error) => {
          captureError(error, 'Get convo list counts failed')
          return empty()
        })
      )
    })
  )
}

export const closeAllConversations: Epic<RootAction, RootAction, RootState, Services> = (action$) =>
  action$.pipe(
    filter(isActionOf(closeAllConversationsAsync.request)),
    mergeMap(({ payload: { subscriptionId } }) =>
      from(api.closeAllConversations(subscriptionId)).pipe(
        map(() => closeAllConversationsAsync.success()),
        catchError((error) => {
          captureError(error, 'Close all conversations failed')
          return of(closeAllConversationsAsync.failure(error))
        })
      )
    )
  )
